import { colors } from '../../static/constants/theme';
import { typography } from '../../static/constants/typography';

const joinEarlyPageStyles = {
  heroSection: {
    width: 1,
    background: colors.background.gradient,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    p: { xs: '40px 24px 64px', lg: '80px 0 132px 0' },
    gap: { xs: 5, lg: 8 },
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
  },
  decorationImage: {
    position: 'absolute',
    right: 0,
    top: '0%',
    zIndex: 0,
    display: { xs: 'none', lg: 'block' },
  },
  heroContent: {
    maxWidth: 1222,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: { xs: 1.5, sm: 2 },
    px: { xs: 2, sm: 5, lg: 0 },
    position: 'relative',
    zIndex: 1,
  },
  heroTitle: {
    background: colors.white,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    ...typography.montSemiboldHero,
    textAlign: 'center',
  },
  heroSubtitle: {
    color: colors.text.light,
    ...typography.latoRegular18,
    textAlign: 'center',
  },
  mainContent: {
    maxWidth: 1222,
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    gap: { xs: 4, lg: 8 },
    position: 'relative',
    zIndex: 1,
    overflow: 'hidden',
  },
  leftColumn: {
    width: { xs: 1, lg: 479 },
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    order: { xs: 2, lg: 1 },
  },
  whyJoinContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    alignItems: { xs: 'center', lg: 'flex-start' },
  },
  whyJoinTitle: {
    background: colors.accent.orangeGradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    ...typography.montSemibold28,
    textAlign: { xs: 'center', lg: 'left' },
  },
  whyJoinItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    textAlign: { xs: 'center', lg: 'left' },
  },
  whyJoinItemTitle: {
    color: colors.border.lighter,
    ...typography.montSemibold20,
  },
  whyJoinItemText: {
    color: colors.white,
    ...typography.latoRegular18,
  },
  accordionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    py: { xs: 0.5, sm: 1, lg: 2 },
  },
  accordionBox: {
    background: colors.background.dark,
    border: `1px solid ${colors.border.primary}`,
    borderRadius: 3,
    overflow: 'hidden',
  },
  accordionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    p: 2,
    cursor: 'pointer',
    '&:hover': { background: 'rgba(21, 82, 188, 0.1)' },
  },
  accordionTitle: {
    color: colors.white,
    ...typography.montSemibold16,
  },
  accordionContent: {
    px: 7.5,
    pb: 2,
  },
  accordionItemText: {
    color: colors.white,
    ...typography.latoRegular18,
    pb: 1.25
  },
  formContainer: {
    width: { xs: 1, lg: 679 },
    background: colors.white,
    border: `1px solid ${colors.border.form}`,
    borderRadius: 4,
    p: 4,
    minHeight: 700,
    position: 'relative',
    zIndex: 2,
    order: { xs: 1, lg: 2 },
  },
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    textAlign: 'center',
    minHeight: { xs: 550, lg: 750 },
  },
  successTitle: {
    color: colors.text.primary,
    ...typography.montSemibold24,
  },
  successText: {
    color: colors.text.dark,
    ...typography.latoRegular18,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: 1.5, sm: 2.75 },
  },
  formRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: { xs: 1.5, sm: 2 },
  },
  fieldContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  requiredLabel: {
    color: colors.text.primary,
    ...typography.latoRegular18,
  },
  asterisk: {
    color: colors.text.error,
    verticalAlign: 'text-top',
  },
  textField: (hasError: boolean) => ({
    '& .MuiOutlinedInput-root': {
      height: '40px',
      '& fieldset': {
        borderColor: hasError ? colors.text.error : colors.border.input,
        borderRadius: '8px',
      },
      '&:hover fieldset': {
        borderColor: hasError ? colors.text.error : colors.border.input,
      },
      '&.Mui-focused fieldset': {
        borderColor: hasError ? colors.text.error : colors.border.input,
      },
      '& input': {
        padding: '0 12px',
        '&::placeholder': { color: colors.text.placeholder },
      },
      '& input:-webkit-autofill': {
        WebkitBoxShadow: '0 0 0 1000px white inset',
        WebkitTextFillColor: colors.text.primary,
      },
    },
    '& .MuiFormHelperText-root': { display: 'none' },
  }),
  textArea: {
    mt: '10px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      padding: '12px',
      '& fieldset': { borderColor: colors.border.input, borderRadius: '8px' },
      '&:hover fieldset': { borderColor: colors.border.input },
      '&.Mui-focused fieldset': { borderColor: colors.border.input },
      '& textarea': { padding: 0 },
    },
    '& .MuiFormHelperText-root': { display: 'none' },
  },
  errorText: {
    color: colors.text.error,
    ...typography.latoRegular14,
    mt: 0.5,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 1,
    p: '12px 16px',
    bgcolor: colors.error.backgroundLight,
    border: `1px solid ${colors.error.border}`,
    borderRadius: '8px',
    color: colors.text.primary,
    ...typography.latoRegular14,
  },
  errorBoxContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.25,
  },
  errorBoxMessage: {
    color: colors.text.medium,
    ...typography.latoRegular14Alt,
    verticalAlign: 'middle',
  },
  errorBoxTitle: {
    color: colors.text.dark,
    ...typography.montSemibold16,
  },
  errorBoxClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.text.placeholder,
    padding: 0,
    lineHeight: 1,
    fontSize: '16px',
    flexShrink: 0,
    width: '20px',
    height: '20px',
  },
  dropdownBox: (hasError: boolean) => ({
    width: 1,
    padding: '0 12px',
    border: `1px solid ${hasError ? colors.text.error : colors.border.input}`,
    borderRadius: '8px',
    fontFamily: 'Lato',
    fontSize: 16,
    color: colors.text.primary,
    background: colors.white,
    cursor: 'pointer',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    background: colors.white,
    border: `1px solid ${colors.border.input}`,
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    zIndex: 1000,
    maxHeight: 200,
    overflow: 'auto',
  },
  dropdownItem: (isSelected: boolean) => ({
    padding: '10px',
    cursor: 'pointer',
    fontFamily: 'Lato',
    fontSize: 16,
    color: colors.text.primary,
    background: isSelected ? colors.background.selected : 'transparent',
    '&:hover': { background: colors.background.hover },
  }),
  radioGroup: {
    '& .MuiFormControlLabel-label': { color: colors.text.dark },
    '& .MuiRadio-root': { color: colors.text.dark },
    '& .Mui-checked': { color: colors.text.dark },
  },
  checkboxGroup: {
    '& .MuiCheckbox-root': { color: colors.text.dark },
    '& .Mui-checked': { color: colors.text.dark },
    '& .MuiFormControlLabel-label': { color: colors.text.dark },
  },
};

export default joinEarlyPageStyles;
